/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import { useAuth } from "hooks/useAuth";
import apiClient from "Api"
import { useEffect, useState } from "react";
import Loading from "components/Loading";
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { useSnackbar } from 'notistack';
import { date } from "yup";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";

function DashboardAdmin() {
  const [isLoading, setIsLoading] = useState(false);
  const { sales, tasks } = reportsLineChartData;
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [hasPendingAppConfiguration, setHasPendingAppConfiguration] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    activeApps: 0,
    installedAppsMonth: 0,
    uninstalledAppsMonth: 0,
    daySalesCount: 0,
    totalDaySales: 0,
    monthSalesCount: 0,
    totalMonthSales: 0,
    appGroupByType: {},
    totalGroupedByTenant: {}
  })

  const dateTime = new Date().toLocaleString();

  function loadData() {
    setIsLoading(true);
    apiClient.get(`/account/currentuser`).then(responseCurrentUser => {
      setHasPendingAppConfiguration(responseCurrentUser.data.hasPendingAppConfiguration);

      apiClient.get(`/tenants/dashboard`).then(responseDashboard => {
        responseDashboard.data.totalGroupedByTenant.labels = responseDashboard.data.totalGroupedByTenant.labels.map((item) => { return item.replace(".myshopify.com", "") });

        setDashboardData(responseDashboard.data);
        setIsLoading(false);
      }).catch(e => {
        setIsLoading(false);
        enqueueSnackbar('Erro ao retornar dados do dashboard!', { variant: "error" });
      });
    }).catch(e => {
      setIsLoading(false);
      enqueueSnackbar('Erro ao retornar dados do usuário!', { variant: "error" });
    });
  }

  useEffect(() => {
    loadData();
  }, []);

  return (
    <DashboardLayout>
      <Loading isLoading={isLoading}></Loading>
      <DashboardNavbar />
      {!isLoading &&
        <MDBox py={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="primary"
                  icon="today"
                  title="Total apps ativos"
                  count={dashboardData.activeApps}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  icon="calendar_month"
                  title="Apps instalados mês"
                  count={dashboardData.installedAppsMonth}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="warning"
                  icon="pending_actions"
                  title="Apps desinstalados no mês"
                  count={dashboardData.uninstalledAppsMonth}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="success"
                  icon="paid"
                  title="Total transacionado no dia"
                  count={dashboardData.daySalesCount}
                  percentage={{ color: "success", amount: dashboardData.totalDaySales.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }), label: "Total" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="success"
                  icon="paid"
                  title="Total transacionado no mês"
                  count={dashboardData.monthSalesCount}
                  percentage={{ color: "success", amount: dashboardData.totalMonthSales.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }), label: "Total" }}
                />
              </MDBox>
            </Grid>
          </Grid>
          <MDBox mt={4.5}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox mb={3}>
                  <VerticalBarChart
                    color="info"
                    title="Apps/Forma de pagamento"
                    description=""
                    chart={dashboardData.appGroupByType}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox mb={3}>
                  <VerticalBarChart
                    color="success"
                    title="Top 10 Vendas/Loja (mês atual)"
                    chart={dashboardData.totalGroupedByTenant}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <Grid container spacing={3}>
            {dashboardData.conversionPix != null &&
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="secondary"
                    icon="pix"
                    title="Taxa de conversão PIX (mês atual)"
                    count={dashboardData.conversionPix + "%"}
                  />
                </MDBox>
              </Grid>}
            {dashboardData.conversionBoleto != null &&
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="secondary"
                    icon="receipt"
                    title="Taxa de conversão BOLETO (mês atual)"
                    count={dashboardData.conversionBoleto + "%"}
                  />
                </MDBox>
              </Grid>}
            {dashboardData.conversionCreditCard != null &&
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={1.5}>
                  <ComplexStatisticsCard
                    color="secondary"
                    icon="credit_card"
                    title="Taxa de conversão CARTÃO DE CRÉDITO (mês atual)"
                    count={dashboardData.conversionCreditCard + "%"}
                  />
                </MDBox>
              </Grid>}
          </Grid>
        </MDBox>}
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardAdmin;
